<template>
  <div>
    <!-- Top Row: Action Button + Search/Filter/Sort in one line -->
    <div class="row align-items-center mb-3">
      <!-- Left Column: Add Document/Photos Button -->
      <div class="col-auto">
        <b-button
          v-if="dataObj.actionButton"
          :id="$route.name + '-tabs-add-new-location'"
          size="md"
          @click.prevent="onActionItemClicked(dataObj.actionButton)"
          class="ss-warning-bg border-0"
        >
          <i class="fa fa-plus u-mr-xsmall"></i>
          {{ dataObj.actionButton.menuTitle }}
        </b-button>
      </div>

      <!-- Right Column: Search Box + Filter Dropdown + Sort Dropdown -->
      <!-- Only show these options if there are 5 or more documents -->
      <div
        class="col d-flex justify-content-end align-items-center"
        style="gap: 10px;"
        v-if="documents.length >= 5"
      >
        <!-- Smaller Search Box -->
        <b-form-input
          v-model="searchQuery"
          placeholder="Search by name or description"
          style="width: 350px;"
        />

        <!-- v-select for Filter (Document Type) -->
        <v-select
          v-model="selectedType"
          :options="documentTypeOptions"
          placeholder="Filter by"
          :clearable="false"
          label="text"
          :reduce="option => option.value"
          style="width: 250px;"
        />

        <!-- v-select for Sort -->
        <v-select
          v-model="selectedSort"
          :options="sortOptions"
          placeholder="Sort by"
          :clearable="false"
          label="text"
          :reduce="option => option.value"
          style="width: 150px;"
        />
      </div>
    </div>

    <!-- Document Table -->
    <div class="table-responsive table-responsive-md w-auto dashboard-table">
      <table class="table" id="datatable">
        <thead class="c-table__head c-table__head--slim">
          <tr class="c-table__row">
            <th class="c-table__cell--head text-center" width="5%"></th>
            <th class="c-table__cell--head text-left" width="10%">Document Name</th>
            <th class="c-table__cell--head text-left" width="10%">Description</th>
            <th class="c-table__cell--head text-center" width="10%">Document Type</th>
            <th class="c-table__cell--head text-center" width="10%">Date/Time</th>
            <th class="c-table__cell--head text-right" width="1%"></th>
            <th class="c-table__cell--head text-right" width="5%">Delete</th>
          </tr>
        </thead>
        <tbody>
          <template v-if="filteredDocuments.length > 0">
            <tr
              class="c-table__row"
              v-for="document in filteredDocuments"
              :key="document.documentId"
            >
              <td class="align-middle text-center">
                <a
                  :id="`view-document-${document.documentId}`"
                  @click.prevent="showPreview(document)"
                  href="#"
                >
                  <i class="fa fa-picture-o" aria-hidden="true"></i> View
                </a>
              </td>
              <td class="text-left menu-icons pl-2">{{ document.documentName }}</td>
              <td class="align-middle">{{ document.notes }}</td>
              <td class="text-nowrap align-middle text-center">{{ document.type }}</td>
              <td class="align-middle text-center">{{ document.displayCreatedDate }}</td>
              <td class="align-middle text-right">
                <i
                  @click="editDocument(document)"
                  class="fa fa-pencil u-text-primary u-text-larger d-block clickable"
                  aria-hidden="true"
                ></i>
              </td>
              <td class="align-middle text-right">
                <i
                  v-if="document.isDeletable"
                  @click="deleteItem(document)"
                  class="fa fa-trash u-text-danger u-text-larger d-block clickable"
                  aria-hidden="true"
                ></i>
              </td>
            </tr>
          </template>
          <template v-else>
            <tr class="c-table__row">
              <td class="text-center" colspan="7">No documents found</td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
  import EVENTS from "@/constants/events";
  import GlobalServices from "@/services/global";
  import editMixin from "@/mixin/edit-mixin";
  import MODULE from "@/constants/modules";
  import ApplicationService from "@/services/application";
  import vSelect from "vue-select";

  const global = new GlobalServices();
  const app = new ApplicationService();

  export default {
    mixins: [editMixin],
    props: {
      dataObj: Object,
    },
    components: {
      vSelect,
    },
    data() {
      return {
        id: 0,
        EVENT_ACTION: {},
        intervalApi: null,
        documents: [],
        modalStatus: {
          hideShowModal: false,
          modalName: "",
          modalTitle: "",
          data: {},
        },
        document: {
          url: "",
          wordURL: "",
          documentName: "",
        },
        // Search, filter, and sort
        searchQuery: "",
        selectedType: null,
        selectedSort: "name",

        // Document type options set on mounted, includes "Show All" (value null)
        documentTypeOptions: [],
        // Sort options for v-select
        sortOptions: [
          { value: "name", text: "Sort by Name" },
          { value: "date", text: "Sort by Date" },
          { value: "type", text: "Sort by Type" },
        ],
      };
    },
    computed: {
      /**
       * Returns documents filtered by the search query and document type,
       * then sorted by the selected sort option.
       */
      filteredDocuments() {
        // 1. Filter by search
        let filtered = this.documents.filter((doc) => {
          const search = this.searchQuery.toLowerCase();
          const docName = doc.documentName ? doc.documentName.toLowerCase() : "";
          const docDesc = doc.notes ? doc.notes.toLowerCase() : "";
          return docName.includes(search) || docDesc.includes(search);
        });
        // 2. Filter by selected type (if not null)
        if (this.selectedType) {
          filtered = filtered.filter((doc) => doc.type === this.selectedType);
        }
        // 3. Sort by selected option
        if (this.selectedSort === "name") {
          filtered.sort((a, b) => a.documentName.localeCompare(b.documentName));
        } else if (this.selectedSort === "date") {
          filtered.sort(
            (a, b) => new Date(b.displayCreatedDate) - new Date(a.displayCreatedDate)
          );
        } else if (this.selectedSort === "type") {
          filtered.sort((a, b) => a.type.localeCompare(b.type));
        }
        return filtered;
      },
    },
    methods: {
      onActionItemClicked(action) {
        app.actionNavigation(action);
      },
      deleteItem(data) {
        this.updateField(
          "IsDeleted",
          true,
          "Deleted Document",
          "deleted",
          null,
          null,
          null,
          null,
          data.id,
          MODULE.DOCUMENTS.ActionId
        );
      },
      showPreview(document) {
        if (document) {
          if (document.documentType !== "Generated") {
            this.showModal(this.EVENT_ACTION.Image.View, "Image Gallery", "", document);
          } else {
            this.showModal(this.EVENT_ACTION.PDF.View, "PDF", "", document);
          }
        }
      },
      editDocument(document) {
        this.showModal(this.EVENT_ACTION.DOCUMENTS_PHOTOS.Edit, "Edit Document", "", document);
      },
      showModal(paramModalName, modalTitle, sendButtonText, data) {
        this.modalStatus.hideShowModal = true;
        this.modalStatus.modalName = paramModalName;
        this.modalStatus.modalTitle = modalTitle;
        this.modalStatus.modalSaveButtonTitle = sendButtonText;
        this.modalStatus.data = data;
        this.modalStatus.dataListing = this.documents;
        this.$store.dispatch("toggleModalStatus", this.modalStatus);
      },
      /**
       * Loads document data from the API, storing the array in this.documents.
       */
      async loadData() {
        const results = await global.getDataByActionId(this.dataObj.param, true);
        this.documents = results.table.data.map((document) => {
          return {
            documentId: parseInt(document.id),
            id: parseInt(document.id),
            documentName: document.name,
            url: document.url,
            displayCreatedDate: document.createdDate,
            description: document.description,
            size: document.size || 0,
            // If extension is PDF, treat it as "Generated," else "Attachments"
            documentType:
              document.url.split(".").pop() === "pdf" ? "Generated" : "Attachments",
            type: document.type,
            isDeletable: document.isDeletable,
            icon: document.icon,
            notes: document.notes,
          };
        });
      },
      addDocument() {
        // Placeholder for adding a document
      },
    },
    async created() {
      await this.loadData();
    },
    async mounted() {
      this.EVENT_ACTION = EVENTS;

      // Load document types and add a "Show All" option.
      const documentTypes = await global.getUXDropDown(MODULE.DOCUMENTS.UX.DOCTYPES);

      // 1. Sort the returned list alphabetically by displayName
      documentTypes.sort((a, b) => a.displayName.localeCompare(b.displayName));

      // 2. Build the final array with "Show All" pinned at the top
      this.documentTypeOptions = [
        { value: null, text: "Show All" },
        ...documentTypes.map((type) => {
          return {
            value: type.displayName,
            text: type.displayName,
          };
        }),
      ];

      // Auto-refresh documents every 5 seconds.
      this.intervalApi = setInterval(async () => {
        await this.loadData();
      }, 5000);
    },
    beforeDestroy() {
      clearInterval(this.intervalApi);
    },
  };
</script>
